import { Controller } from "@hotwired/stimulus"

// Reader mode: moves the deliverable card into a full-screen overlay
// and hides non-essential sections tagged with [data-reader-hide].
export default class extends Controller {
  connect() {
    this._escHandler = this._escHandler || (e => {
      if (e.key === 'Escape') this.exit()
    })

    // Cache scrollable wrappers whose height is capped in normal mode
    this._wrappers = []
  }

  enter(event) {
    if (event) event.preventDefault()
    if (this.active) return
    this.active = true

    // Create overlay
    this.overlay = document.createElement('div')
    this.overlay.className = 'fixed inset-0 z-50 bg-white overflow-y-auto'

    // Inner container to keep a good reading width
    this.inner = document.createElement('div')
    this.inner.className = 'max-w-4xl w-full mx-auto p-4 sm:p-6'
    this.overlay.appendChild(this.inner)

    // Placeholder to restore position later
    this.placeholder = document.createComment('reader-mode-placeholder')
    this.element.parentNode.insertBefore(this.placeholder, this.element)

    // Move the card into overlay
    this.inner.appendChild(this.element)

    // Hide non-essential sections (force with inline style to be safe)
    this.hiddenEls = Array.from(document.querySelectorAll('[data-reader-hide]'))
    this.hiddenEls.forEach(el => this._hideEl(el))

    // Fallback: hide the card's direct header row if present,
    // and track it so we can restore on exit
    Array.from(this.element.children).forEach(child => {
      if (child.classList && child.classList.contains('border-b')) {
        this._hideEl(child)
        this.hiddenEls.push(child)
      }
    })

    // Hide any explicit "enter full screen" triggers
    this.enterButtons = Array.from(document.querySelectorAll('[data-reader-enter]'))
    this.enterButtons.forEach(btn => this._hideEl(btn))

    // Relax max-height on tab wrapper(s) to allow full scroll
    this._wrappers = []
    this.element.querySelectorAll('.bg-gray-50').forEach(el => {
      if (el.classList.contains('rounded-b')) {
        this._wrappers.push({ el, cls: el.className })
        el.classList.remove('max-h-96', 'overflow-y-auto')
        el.classList.add('max-h-none', 'overflow-visible')
      }
    })

    // Add exit button
    this.exitBtn = document.createElement('button')
    this.exitBtn.type = 'button'
    this.exitBtn.className = 'fixed top-3 right-3 z-50 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white shadow hover:bg-gray-50'
    this.exitBtn.innerHTML = `
      <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4h4M4 4l6 6M20 16v4h-4m4 0l-6-6"/>
      </svg>
      Exit full screen`
    this.exitBtn.addEventListener('click', () => this.exit())
    this.overlay.appendChild(this.exitBtn)

    document.body.appendChild(this.overlay)
    document.body.classList.add('overflow-hidden')
    window.addEventListener('keyup', this._escHandler)
  }

  exit(event) {
    if (event) event.preventDefault()
    if (!this.active) return

    // Restore wrappers
    this._wrappers.forEach(({ el, cls }) => { el.className = cls })
    this._wrappers = []

    // Extra safety: ensure tab wrapper returns to capped height
    // in case dynamic classes changed while in reader mode
    this.element.querySelectorAll('.bg-gray-50').forEach(el => {
      if (el.classList.contains('rounded-b')) {
        el.classList.remove('max-h-none', 'overflow-visible')
        if (!el.classList.contains('max-h-96')) el.classList.add('max-h-96')
        if (!el.classList.contains('overflow-y-auto')) el.classList.add('overflow-y-auto')
      }
    })

    // Unhide sections
    if (this.hiddenEls) this.hiddenEls.forEach(el => this._showEl(el))
    if (this.enterButtons) this.enterButtons.forEach(btn => this._showEl(btn))

    // Move element back
    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.parentNode.insertBefore(this.element, this.placeholder)
      this.placeholder.remove()
    }

    // Remove overlay
    if (this.overlay) this.overlay.remove()

    document.body.classList.remove('overflow-hidden')
    window.removeEventListener('keyup', this._escHandler)
    this.active = false
  }

  toggle(event) {
    if (this.active) {
      this.exit(event)
    } else {
      this.enter(event)
    }
  }

  // Helpers to hide/show with inline style so utility classes can't override
  _hideEl(el) {
    if (!el) return
    if (el.dataset.readerPrevDisplay === undefined) {
      el.dataset.readerPrevDisplay = el.style.display || ''
    }
    el.classList.add('hidden')
    el.style.setProperty('display', 'none', 'important')
  }

  _showEl(el) {
    if (!el) return
    el.classList.remove('hidden')
    if (el.dataset.readerPrevDisplay !== undefined) {
      el.style.display = el.dataset.readerPrevDisplay
      delete el.dataset.readerPrevDisplay
    } else {
      el.style.removeProperty('display')
    }
  }
}
